// src/hooks/useProductFilters.js
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

/** -------- Helpers -------- **/

// Case-insensitive sort in NL, met numeriek begrip
const compare = (a, b) =>
  String(a ?? "").localeCompare(String(b ?? ""), "nl", {
    sensitivity: "base",
    numeric: true,
  });

// Leest CSV of repeated query keys ( ?key=a,b  of  ?key=a&key=b )
const readCsvParam = (sp, key) => {
  const many = sp.getAll(key);
  if (many.length) return many;
  const one = sp.get(key);
  return one ? one.split(",").filter(Boolean) : [];
};

const toCsv = (arr) => (arr && arr.length ? arr.join(",") : undefined);

/**
 * Parse `druif`, bv.:
 * - "80% Nieddera, 20% andere inheemse rode druiven"
 * - "100% Grillo"
 * - "Rkatsiteli / Mtsvane"
 * -> ["Nieddera", "Inheemse rode druiven"], ["Grillo"], ["Rkatsiteli","Mtsvane"]
 */
function parseGrapes(raw) {
  if (!raw) return [];
  const text = String(raw).replace(/\(([^)]+)\)/g, ", $1"); // ( ... ) → , ...
  const parts = text.split(/\s*(?:,|\/|\+|·|&|\s+en\s+|\s+and\s+)\s*/i);
  const out = [];
  for (let p of parts) {
    if (!p) continue;
    p = p
      .replace(/\b\d{1,3}\s*%/gi, "") // 80%
      .replace(/^\s*-\s*|\s*-\s*$/g, "")
      .trim();
    p = p.replace(/^(andere|overige|diverse)\s+/i, ""); // verwijder alleen het voorvoegsel
    if (!p) continue;
    const normalized = p.charAt(0).toUpperCase() + p.slice(1);
    if (!out.includes(normalized)) out.push(normalized);
  }
  return out;
}

/**
 * Inhoud → altijd liters:
 *  - >200  → ml (750 → 0.75 L)
 *  - >10   → cl (75  → 0.75 L)
 *  - anders→ L  (0.75 → 0.75 L)
 */
const normalizeVolumeL = (raw) => {
  const v = Number(raw);
  if (!Number.isFinite(v)) return null;
  if (v > 200) return v / 1000; // ml
  if (v > 10) return v / 100;   // cl
  return v;                     // liter
};

/** -------- Main hook -------- **/

export default function useProductFilters(products, { syncUrl = true } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isTrue = (v) => {
  if (v === true) return true;
  if (v === false || v === 0 || v === "0" || v == null) return false;
  const s = String(v).trim().toLowerCase();
  return s === "1" || s === "true" || s === "ja" || s === "yes" || s === "y" || s === "on";
};

  // Normaliseer input naar array
  const items = useMemo(() => {
    if (Array.isArray(products)) return products;
    if (products && typeof products === "object") return Object.values(products);
    return [];
  }, [products]);

  // Init filters (lees waar mogelijk uit URL)
  const [filters, setFilters] = useState({
    q: searchParams.get("q") || "",
    productTypes: readCsvParam(searchParams, "type"),
    countries:    readCsvParam(searchParams, "land"),
    streken:      readCsvParam(searchParams, "streek"),
    wijnhuizen:   readCsvParam(searchParams, "wijnhuis"),  
    regios:       readCsvParam(searchParams, "regio"),
    grapes:       readCsvParam(searchParams, "grape"),
    wijnsoorten:  readCsvParam(searchParams, "wijnsoort"),
    mousserend:   searchParams.get("mousserend") === "1",
    natuurwijn:   searchParams.get("natuurwijn") === "1",
    minPrice:   Number(searchParams.get("minPrice"))   || 0,
    maxPrice:   Number(searchParams.get("maxPrice"))   || 9999,
    minInhoud:  Number(searchParams.get("minInhoud"))  || 0,
    maxInhoud:  Number(searchParams.get("maxInhoud"))  || 3,    // realistisch max in L
    inStockOnly: searchParams.get("inStockOnly") === "1",
    sort:        searchParams.get("sort") || "recommended",
  });

  // Bouw facetten + bounds
  const { facets, priceBounds, inhoudBounds, productGrapes } = useMemo(() => {
    const sType = new Set();
    const sLand = new Set();
    const sWijnsoort = new Set();
    const sStreek = new Set();
    const sWijnhuis = new Set();
    const sRegio = new Set();
    const sGrape = new Set();

    const grapesMap = new Map(); // product.id → parsed grapes

    let minPrice = Infinity, maxPrice = -Infinity;
    let minVol = Infinity,   maxVol = -Infinity;

    items.forEach((p) => {
      const i = p.product_info ?? p;

      if (i?.product_type) sType.add(String(i.product_type));
      if (i?.land)         sLand.add(String(i.land));
      if (i?.streek)       sStreek.add(String(i.streek));
      if (i?.wijnhuis)     sWijnhuis.add(String(i.wijnhuis));
      if (i?.regio)        sRegio.add(String(i.regio));
      if (i?.wijnsoort) sWijnsoort.add(String(i.wijnsoort));

      const grapes = parseGrapes(i?.druif);
      grapesMap.set(p.id, grapes);
      grapes.forEach((g) => sGrape.add(g));

      const price = Number(i?.prijs ?? p.price ?? 0);
      if (!Number.isNaN(price)) {
        if (price < minPrice) minPrice = price;
        if (price > maxPrice) maxPrice = price;
      }

      const volL = normalizeVolumeL(i?.inhoud);
      if (volL != null) {
        if (volL < minVol) minVol = volL;
        if (volL > maxVol) maxVol = volL;
      }
    });

    return {
      facets: {
        productTypes: Array.from(sType).sort(compare),
        countries:    Array.from(sLand).sort(compare),
        streken:      Array.from(sStreek).sort(compare),
        wijnhuizen:   Array.from(sWijnhuis).sort(compare),
        regios:       Array.from(sRegio).sort(compare),
        grapes:       Array.from(sGrape).sort(compare),
        wijnsoorten:  Array.from(sWijnsoort).sort(compare),
      },
      priceBounds: {
        min: Number.isFinite(minPrice) ? minPrice : 0,
        max: Number.isFinite(maxPrice) ? maxPrice : 9999,
      },
      inhoudBounds: {
        min: Number.isFinite(minVol) ? minVol : 0,
        max: Number.isFinite(maxVol) ? maxVol : 3, // tot 3L (mag je aanpassen)
      },
      productGrapes: grapesMap,
    };
  }, [items]);

  // Clamp numerieke ranges binnen actuele bounds
  useEffect(() => {
    setFilters((f) => {
      const npMin = Math.max(f.minPrice,  priceBounds.min);
      const npMax = Math.min(f.maxPrice,  priceBounds.max);
      const niMin = Math.max(f.minInhoud, inhoudBounds.min);
      const niMax = Math.min(f.maxInhoud, inhoudBounds.max);
      if (npMin === f.minPrice && npMax === f.maxPrice && niMin === f.minInhoud && niMax === f.maxInhoud) {
        return f;
      }
      return { ...f, minPrice: npMin, maxPrice: npMax, minInhoud: niMin, maxInhoud: niMax };
    });
  }, [priceBounds.min, priceBounds.max, inhoudBounds.min, inhoudBounds.max]);

  // Sync filters → URL
  useEffect(() => {
    if (!syncUrl) return;

    const params = {};
    if (filters.q)                     params.q = filters.q;
    if (filters.productTypes.length)   params.type   = toCsv(filters.productTypes);
    if (filters.countries.length)      params.land   = toCsv(filters.countries);
    if (filters.streken.length)        params.streek = toCsv(filters.streken);
    if (filters.wijnhuizen.length)     params.wijnhuis = toCsv(filters.wijnhuizen);
    if (filters.regios.length)         params.regio  = toCsv(filters.regios);
    if (filters.grapes.length)         params.grape  = toCsv(filters.grapes);
    if (filters.wijnsoorten.length) params.wijnsoort = toCsv(filters.wijnsoorten);
    if (filters.mousserend)         params.mousserend = "1";
    if (filters.natuurwijn)         params.natuurwijn = "1";
    if (filters.minPrice)              params.minPrice = String(filters.minPrice);
    if (filters.maxPrice && filters.maxPrice !== 9999)
                                      params.maxPrice = String(filters.maxPrice);
    if (filters.minInhoud)             params.minInhoud = String(filters.minInhoud);
    if (filters.maxInhoud && filters.maxInhoud !== 3)
                                      params.maxInhoud = String(filters.maxInhoud);
    if (filters.inStockOnly)           params.inStockOnly = "1";
    if (filters.sort !== "recommended") params.sort = filters.sort;

    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams, syncUrl]);

  // Filter + sort pipeline
  const filteredProducts = useMemo(() => {

    //let list = items.filter((p) => (p.product_info ?? p)?.actief);
    let list = items;

    console.log(list);

    // free text (naam + summary)
    const q = filters.q.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const name = (p.product_info?.naam ?? p.name ?? "").toLowerCase();
        const summary = (p.summary ?? "").toLowerCase();
        return `${name} ${summary}`.includes(q);
      });
    }

    // facetten (multi)
    if (filters.productTypes.length)
      list = list.filter((p) => filters.productTypes.includes(String(p.product_info?.product_type ?? "")));
    if (filters.countries.length)
      list = list.filter((p) => filters.countries.includes(String(p.product_info?.land ?? "")));
    if (filters.streken.length)
      list = list.filter((p) => filters.streken.includes(String(p.product_info?.streek ?? "")));
    if (filters.wijnhuizen.length)
      list = list.filter((p) => filters.wijnhuizen.includes(String(p.product_info?.wijnhuis ?? "")));
    if (filters.regios.length)
      list = list.filter((p) => filters.regios.includes(String(p.product_info?.regio ?? "")));

    // druif (multi)
    if (filters.grapes.length) {
      list = list.filter((p) => {
        const g = productGrapes.get(p.id) || [];
        return g.some((x) => filters.grapes.includes(x));
      });
    }

    // prijs-range
    list = list.filter((p) => {
      const price = Number(p.product_info?.prijs ?? p.price ?? 0);
      return price >= filters.minPrice && price <= filters.maxPrice;
    });

    // inhoud-range (liters)
    list = list.filter((p) => {
      const volL = normalizeVolumeL(p.product_info?.inhoud);
      if (volL == null) return true; // of false als je onbekende inhoud wil uitsluiten
      return volL >= filters.minInhoud && volL <= filters.maxInhoud;
    });

    // voorraad
    if (filters.inStockOnly) {
      list = list.filter((p) => (p.product_info?.stock ?? 0) > 0);
    }

    // Wijnsoort (multi)
    if (filters.wijnsoorten.length) {
      list = list.filter((p) =>
        filters.wijnsoorten.includes(String((p.product_info ?? p)?.wijnsoort ?? ""))
      );
    }

    // Kenmerken (binair)
    if (filters.mousserend) {
      list = list.filter((p) => isTrue((p.product_info ?? p)?.mousserend));
    }
    if (filters.natuurwijn) {
      list = list.filter((p) => isTrue((p.product_info ?? p)?.natuurwijn));
    }

    // sort
    switch (filters.sort) {
      case "price_asc":
        list.sort(
          (a, b) =>
            (Number(a.product_info?.prijs ?? a.price) || 0) -
            (Number(b.product_info?.prijs ?? b.price) || 0)
        );
        break;
      case "price_desc":
        list.sort(
          (a, b) =>
            (Number(b.product_info?.prijs ?? b.price) || 0) -
            (Number(a.product_info?.prijs ?? a.price) || 0)
        );
        break;
      case "name_asc":
        list.sort((a, b) =>
          compare(a.product_info?.naam ?? a.name, b.product_info?.naam ?? b.name)
        );
        break;
      case "name_desc":
        list.sort((a, b) =>
          compare(b.product_info?.naam ?? b.name, a.product_info?.naam ?? a.name)
        );
        break;
      case "newest": {
        const toTime = (x) => new Date(x || 0).getTime();
        list.sort((a, b) => {
          const ad = toTime(a.product_info?.created_at);
          const bd = toTime(b.product_info?.created_at);
          if (ad || bd) return bd - ad;
          return (b.id ?? 0) - (a.id ?? 0);
        });
        break;
      }
      default:
        // recommended → originele volgorde
        break;
    }

    return list;
  }, [items, filters, productGrapes]);

  return {
    filters,
    setFilters,
    filteredProducts,
    facets,       // { productTypes, countries, streken, wijnhuizen, regios, grapes }
    priceBounds,  // { min, max }
    inhoudBounds, // { min, max }  (in liters)
  };
}
